/**
 * =============================================================================
 *  CHIEU CUA CAC LENH ANH: anh dang NAM O DAU tren man hinh
 * =============================================================================
 *
 *  Cung mot mo hinh voi `editedText` va `pagePlan` trong kho trang thai: KHONG
 *  co lich su thu hai. Vi tri anh tren man hinh duoc SUY RA hoan toan tu danh
 *  sach lenh, moi lan, bang ham thuan tuy o day. Nho vay Ctrl+Z chi la cat bot
 *  danh sach lenh, va o chon tren man hinh tu quay ve dung cho cu.
 *
 * =============================================================================
 *  VI SAO LENH KHONG MANG SAN HOP CHU NHAT
 * =============================================================================
 *  Se de hon nhieu neu 'content.image.transform' mang luon hop chu nhat moi:
 *  lop hien thi doc thang, khoi phai tinh gi. Nhung luc do MOT thao tac se co
 *  HAI nguon su that - ma tran (thu duong ghi dung) va hop chu nhat (thu man
 *  hinh dung) - va khong co gi bat chung phai khop nhau. Do dung la bai hoc so 3
 *  cua du an, da tra gia ba lan.
 *
 *  Nen chi ma tran duoc luu, va hop chu nhat duoc TINH LAI tu no. Neu duong ghi
 *  dat anh o mot cho, man hinh se ve dung cho do - khong co duong nao khac.
 */
import type { EditCommand, PdfMatrix, PdfRect } from '@pdfeditor/types';
import { inverse, mul, unitSquareBounds } from './matrix';

/** Mot anh nhu no ton tai TRONG TEP GOC, truoc moi lenh. */
export interface ImageBaseline {
  pageIndex: number;
  /** So thu tu toan tu `Do` - chinh la dinh danh trong lenh. */
  occurrence: number;
  /** Ma tran DAY DU tu danh sach toan tu cua pdf.js. */
  fullMatrix: PdfMatrix;
  /** Ma tran `cm` CUC BO doc tu luong noi dung. null = khong doc duoc. */
  localMatrix: PdfMatrix | null;
  /**
   * Ly do RIENG CUA ANH NAY khong keo duoc, hoac null.
   *
   * Tach khoi ly do cua CA TRANG (`PageImagesInfo.refusal`) mot cach co chu
   * dich: mot trang co the co nam anh keo duoc va mot anh bi duong cat bao
   * quanh. Dung mot ly do chung cho ca trang o day se tat nham bon anh lanh,
   * con dung ly do cua trang cho rieng anh do se cho nguoi dung keo mot thu
   * khong keo duoc.
   */
  transformRefusal?: string | null;
}

export interface ProjectedImage extends ImageBaseline {
  /** Hop chu nhat DANG CO HIEU LUC, he toa do PDF. */
  rect: PdfRect;
  /** Ma tran `cm` dang co hieu luc. Bang localMatrix khi chua co lenh nao. */
  currentLocal: PdfMatrix | null;
  /** true = da bi xoa boi mot lenh. Lop hien thi khong duoc ve no nua. */
  removed: boolean;
}

/**
 * Hop chu nhat moi cua mot anh sau khi `cm` cuc bo doi tu `oldLocal` sang
 * `newLocal`.
 *
 * Suy dien: goi outer la phan bien doi bao ngoai, full = local x outer. Phan
 * bao ngoai KHONG doi (khong ai sua no), nen
 *      newFull = newLocal x outer = newLocal x (local^-1 x full)
 * Ca ba thua so deu biet. Khong can doan phan bao ngoai la gi - va do la diem
 * mau chot, vi no co the chua ca phep xoay trang.
 *
 * Tra ve null khi ma tran cu suy bien (anh bi bop ve be rong 0): luc do khong
 * co phep nghich dao, va doan bua mot con so se dat anh vao mot cho ngau nhien.
 */
export function projectRect(
  oldLocal: PdfMatrix,
  fullMatrix: PdfMatrix,
  newLocal: PdfMatrix,
): PdfRect | null {
  const invLocal = inverse(oldLocal);
  if (!invLocal) return null;
  return unitSquareBounds(mul(newLocal, mul(invLocal, fullMatrix)));
}

/**
 * Ap danh sach lenh len cac anh goc cua MOT trang.
 *
 * Lenh cua trang khac bi bo qua - ben goi truyen ca danh sach vao duoc ma khong
 * phai loc truoc.
 */
export function projectImages(
  baselines: readonly ImageBaseline[],
  commands: readonly EditCommand[],
): ProjectedImage[] {
  const out = baselines.map(
    (b): ProjectedImage => ({
      ...b,
      rect: unitSquareBounds(b.fullMatrix),
      currentLocal: b.localMatrix,
      removed: false,
    }),
  );

  for (const command of commands) {
    if (command.kind !== 'content.image.transform' && command.kind !== 'content.image.remove') {
      continue;
    }
    const target = out.find(
      (i) => i.pageIndex === command.pageIndex && i.occurrence === command.occurrence,
    );
    if (!target) continue;

    if (command.kind === 'content.image.remove') {
      target.removed = true;
      continue;
    }

    // Mot anh da bi xoa roi lai duoc di chuyen la mot day lenh vo nghia, nhung
    // no TON TAI duoc: xoa, hoan tac, di chuyen, lam lai. Giu nguyen trang thai
    // da xoa va van ghi nhan ma tran moi, de neu lenh xoa bi go ra thi anh tro
    // lai o dung cho nguoi dung da keo no toi.
    if (!target.localMatrix) continue;
    const rect = projectRect(target.localMatrix, target.fullMatrix, command.matrix);
    if (!rect) continue;
    target.currentLocal = command.matrix;
    target.rect = rect;
  }

  return out;
}

/**
 * Ma tran `cm` CUC BO can ghi de anh nam dung `toRect`.
 *
 * Dung DUNG mot cong thuc voi projectRect(), chi la chieu nguoc lai - hai chieu
 * nay phai la nghich dao cua nhau, va test chot lai dieu do. Neu chung lech
 * nhau, anh se nhay mot doan nho moi lan nguoi dung nha chuot: man hinh ve theo
 * chieu nay, lan ve sau doc theo chieu kia.
 */
export function localMatrixForRect(
  currentLocal: PdfMatrix,
  currentFull: PdfMatrix,
  toRect: PdfRect,
): PdfMatrix | null {
  const fromRect = unitSquareBounds(currentFull);
  if (Math.abs(fromRect.width) < 1e-9 || Math.abs(fromRect.height) < 1e-9) return null;
  const sx = toRect.width / fromRect.width;
  const sy = toRect.height / fromRect.height;
  const t: PdfMatrix = [sx, 0, 0, sy, toRect.x - fromRect.x * sx, toRect.y - fromRect.y * sy];
  const invFull = inverse(currentFull);
  if (!invFull) return null;
  return mul(mul(mul(currentFull, t), invFull), currentLocal);
}

/**
 * Ma tran DAY DU dang co hieu luc cua mot anh da bi di chuyen.
 *
 * Can khi nguoi dung keo anh LAN THU HAI: lan keo thu hai phai tinh tu vi tri
 * hien tai, khong phai tu vi tri goc trong tep.
 */
export function currentFullMatrix(image: ProjectedImage): PdfMatrix | null {
  if (!image.localMatrix || !image.currentLocal) return null;
  const invLocal = inverse(image.localMatrix);
  if (!invLocal) return null;
  return mul(image.currentLocal, mul(invLocal, image.fullMatrix));
}
